import { differenceBy, sortBy } from "lodash";
import { useMemo } from "react";

import { getKeys } from "../../../utils/object";
import { materialsMapReverse } from "../../events/materials-map";
import {
  Event,
  EventMaterial,
  EventsFiltersForm,
  EventsTimeInterval,
  EventType,
} from "../../events/types";

export function useFilteredEvents(events: Event[], appliedFilters: EventsFiltersForm) {
  const { timeInterval, search, eventType, materials, country } = appliedFilters;

  const timeIntervalEvents = useMemo(() => {
    const isFuture = timeInterval === EventsTimeInterval.FUTURE_EVENTS;
    const now = new Date().getTime();
    const eventsToReturn = events.filter((event) => {
      const endDate = new Date(event.endDateEvent).getTime();
      return isFuture ? endDate >= now : endDate < now;
    });
    const direction = isFuture ? 1 : -1;
    return sortBy(eventsToReturn, (e) => new Date(e.startDateEvent).getTime() * direction);
  }, [events, timeInterval]);

  const filteredEvents = useMemo(() => {
    return timeIntervalEvents.filter((e) => {
      if (search && !e.title.toLowerCase().match(search.toLowerCase())) {
        return false;
      }
      if (
        eventType[EventType.DIGITAL] &&
        eventType[EventType.IN_PERSON] &&
        e.eventType !== EventType.BOTH
      ) {
        return false;
      }
      if (eventType[EventType.DIGITAL] && e.eventType === EventType.IN_PERSON) {
        return false;
      }
      if (eventType[EventType.IN_PERSON] && e.eventType === EventType.DIGITAL) {
        return false;
      }
      if (country && e.country !== country) {
        return false;
      }

      // Filter by material can only be performed while event names are in english language
      const activeMaterials = getKeys(materials).reduce<EventMaterial[]>((acc, key) => {
        if (materials[key]) {
          acc.push({
            Name: materialsMapReverse[key].label,
          });
        }
        return acc;
      }, []);

      return differenceBy(activeMaterials, e.materials, "Name").length === 0;
    });
  }, [timeIntervalEvents, search, eventType, country, materials]);

  return useMemo(
    () => ({
      timeIntervalEvents,
      filteredEvents,
    }),
    [filteredEvents, timeIntervalEvents]
  );
}
